import React, { useState } from "react";
import axios from "axios";
import { ArrowRight, WhatsappLogo, Clock, EnvelopeSimple, MapPin } from "@phosphor-icons/react";
import { toast } from "sonner";
import { useLang } from "@/lib/LanguageContext";
import { wa } from "@/lib/pfd";
import PageShell from "@/components/PageShell";
import ImageHero from "@/components/ImageHero";
import Seo from "@/components/Seo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ContactPage() {
  const { t, lang } = useLang();
  const c = t.contact;
  const [form, setForm] = useState({ name: "", email: "", phone: "", service: "", timing: "", frequency: "", country: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const onChange = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (loading) return;
    if (!form.name || !form.email || form.message.length < 5) {
      toast.error(c.errorToastFields);
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, form);
      if (window.gtag) {
        window.gtag("event", "consultation_form_submit", {
          service: form.service || "unspecified",
          timing: form.timing || "unspecified",
          frequency: form.frequency || "unspecified",
          country: form.country || "unspecified",
          language: lang,
          page_path: window.location.pathname,
        });
      }
      setSent(true);
      toast.success(c.successToast);
      setForm({ name: "", email: "", phone: "", service: "", timing: "", frequency: "", country: "", message: "" });
    } catch {
      toast.error(c.errorToastNetwork);
    } finally {
      setLoading(false);
    }
  };

  let submitLabel = c.send;
  if (loading) submitLabel = c.sending;
  else if (sent) submitLabel = c.sent;

  return (
    <PageShell>
      <Seo title={c.seoTitle} description={c.seoDesc} />

      <ImageHero testId="contact-hero" titleTestId="contact-headline" image="windowView" eyebrow={c.eyebrow} titleA={c.titleA} titleB={c.titleB} text={c.intro} />

      <section data-testid="contact-section" className="py-16 sm:py-32 px-6 lg:px-10">
        <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <a
              data-testid="contact-whatsapp-cta"
              href={wa(c.waMsg)}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-3 bg-[#25D366] text-white px-8 py-4 text-sm font-medium hover:bg-[#20BD5A] transition-colors"
            >
              <WhatsappLogo size={18} weight="fill" />
              {c.waCta}
              <ArrowRight size={16} />
            </a>

            <div className="mt-12 space-y-6">
              <div className="flex items-start gap-4">
                <Clock size={22} className="text-stone-900 mt-0.5 shrink-0" />
                <div>
                  <div className="text-sm font-medium text-stone-900">{c.hoursLabel}</div>
                  <div className="text-sm text-stone-600 mt-1 leading-relaxed">
                    {c.hoursLine1}
                    <br />
                    {c.hoursLine2}
                  </div>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <MapPin size={22} className="text-stone-900 mt-0.5 shrink-0" />
                <div>
                  <div className="text-sm font-medium text-stone-900">{c.basedLabel}</div>
                  <div className="text-sm text-stone-600 mt-1">{c.basedLine}</div>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <EnvelopeSimple size={22} className="text-stone-900 mt-0.5 shrink-0" />
                <div>
                  <div className="text-sm font-medium text-stone-900">{c.waLabel}</div>
                  <div className="text-sm text-stone-600 mt-1">{c.waNumber}</div>
                </div>
              </div>
            </div>
          </div>

          <div className="lg:col-span-7 lg:pl-8">
            <form data-testid="contact-form" onSubmit={onSubmit} className="bg-stone-100 border border-stone-200 p-8 sm:p-12">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <div>
                  <label className="eyebrow block mb-2" htmlFor="name">{c.nameLabel}</label>
                  <input
                    data-testid="contact-name"
                    id="name"
                    name="name"
                    type="text"
                    value={form.name}
                    onChange={onChange}
                    required
                    className="w-full bg-transparent border-0 border-b border-stone-300 py-3 text-stone-900 placeholder-stone-400 focus:outline-none focus:border-stone-900 transition-colors"
                    placeholder={c.namePh}
                  />
                </div>
                <div>
                  <label className="eyebrow block mb-2" htmlFor="email">{c.emailLabel}</label>
                  <input
                    data-testid="contact-email"
                    id="email"
                    name="email"
                    type="email"
                    value={form.email}
                    onChange={onChange}
                    required
                    className="w-full bg-transparent border-0 border-b border-stone-300 py-3 text-stone-900 placeholder-stone-400 focus:outline-none focus:border-stone-900 transition-colors"
                    placeholder={c.emailPh}
                  />
                </div>
              </div>

              <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-6">
                <div>
                  <label className="eyebrow block mb-2" htmlFor="phone">{c.phoneLabel}</label>
                  <input
                    data-testid="contact-phone"
                    id="phone"
                    name="phone"
                    type="tel"
                    value={form.phone}
                    onChange={onChange}
                    className="w-full bg-transparent border-0 border-b border-stone-300 py-3 text-stone-900 placeholder-stone-400 focus:outline-none focus:border-stone-900 transition-colors"
                    placeholder={c.phonePh}
                  />
                </div>
                <div>
                  <label className="eyebrow block mb-2" htmlFor="frequency">{c.frequencyLabel}</label>
                  <select
                    data-testid="contact-frequency"
                    id="frequency"
                    name="frequency"
                    value={form.frequency}
                    onChange={onChange}
                    className="w-full bg-transparent border-0 border-b border-stone-300 py-3 text-stone-900 focus:outline-none focus:border-stone-900 transition-colors"
                  >
                    <option value="">—</option>
                    {c.frequencyOptions.map((o) => (
                      <option key={o} value={o}>{o}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="mt-8">
                <label className="eyebrow block mb-2" htmlFor="service">{c.serviceLabel}</label>
                <select
                  data-testid="contact-service"
                  id="service"
                  name="service"
                  value={form.service}
                  onChange={onChange}
                  className="w-full bg-transparent border-0 border-b border-stone-300 py-3 text-stone-900 focus:outline-none focus:border-stone-900 transition-colors"
                >
                  <option value="">{c.servicePh}</option>
                  {t.services.items.map((s) => (
                    <option key={s.id} value={s.title}>{s.title}</option>
                  ))}
                  <option value="Other">{c.serviceOther}</option>
                </select>
              </div>

              <div className="mt-8">
                <label className="eyebrow block mb-2" htmlFor="country">{c.countryLabel}</label>
                <select
                  data-testid="contact-country"
                  id="country"
                  name="country"
                  value={form.country}
                  onChange={onChange}
                  className="w-full bg-transparent border-0 border-b border-stone-300 py-3 text-stone-900 focus:outline-none focus:border-stone-900 transition-colors"
                >
                  <option value="">{c.countryPh}</option>
                  {c.countryOptions.map((o) => (
                    <option key={o} value={o}>{o}</option>
                  ))}
                  <option value="Rest of the world">{c.countryOther}</option>
                </select>
              </div>

              <div className="mt-8">
                <label className="eyebrow block mb-2" htmlFor="timing">{c.timingLabel}</label>
                <input
                  data-testid="contact-timing"
                  id="timing"
                  name="timing"
                  type="text"
                  value={form.timing}
                  onChange={onChange}
                  className="w-full bg-transparent border-0 border-b border-stone-300 py-3 text-stone-900 placeholder-stone-400 focus:outline-none focus:border-stone-900 transition-colors"
                  placeholder={c.timingPh}
                />
              </div>

              <div className="mt-8">
                <label className="eyebrow block mb-2" htmlFor="message">{c.messageLabel}</label>
                <textarea
                  data-testid="contact-message"
                  id="message"
                  name="message"
                  rows={5}
                  value={form.message}
                  onChange={onChange}
                  required
                  className="w-full bg-transparent border-0 border-b border-stone-300 py-3 text-stone-900 placeholder-stone-400 focus:outline-none focus:border-stone-900 transition-colors resize-none"
                  placeholder={c.messagePh}
                />
              </div>

              <div className="mt-10 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <p className="text-xs text-stone-500 max-w-xs">{c.privacy}</p>
                <button
                  data-testid="contact-submit"
                  type="submit"
                  disabled={loading}
                  className="inline-flex items-center justify-center gap-3 bg-slate-900 text-stone-50 px-8 py-4 text-sm font-medium hover:bg-slate-800 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {submitLabel}
                  {!loading && <ArrowRight size={16} />}
                </button>
              </div>
            </form>
          </div>
        </div>
      </section>
    </PageShell>
  );
}
